import { mergeAttributes } from "@tiptap/core";
import CharacterCount from "@tiptap/extension-character-count";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from "@tiptap/extension-table";
import { useEditor, EditorContent, generateText } from "@tiptap/react";
import type { Editor, JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Table as TableIcon,
  Unlink,
} from "lucide-react";
import { useMemo, useState } from "react";

import { ArticleBody } from "~/components/cih/article-body";
import { cn } from "~/lib/cn";
import { resolveLinkAttrs } from "~/lib/shared/link-rel";

import { Button } from "./button";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

const tableExtensions = [
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
];

/**
 * Inline image node. `markdownToTiptap` emits images wrapped in a paragraph
 * (markdown-it's inline model) and the public `ArticleBody` renderer draws them,
 * so the editor/viewer schema must accept image nodes — otherwise ProseMirror's
 * `fromJSON` hits "Unknown node type: image" and the editor inits empty.
 */
const ArticleImage = Image.configure({ inline: true });

/**
 * Link extension that applies the platform link attribute policy at render
 * time: external destinations get `target="_blank"` + `noopener noreferrer`
 * (editorial — dofollow); owned-domain/relative hrefs are left as authored.
 */
const PolicyLink = Link.extend({
  renderHTML({ HTMLAttributes }) {
    const href =
      typeof HTMLAttributes.href === "string" ? HTMLAttributes.href : "";
    const policy = resolveLinkAttrs(href);
    return [
      "a",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, policy),
      0,
    ];
  },
});

/**
 * The article schema extensions, shared by the editor, viewer, and character
 * counter. Exported so tests can assert stored article bodies load under this
 * exact schema (see rich-text-editor.image.test.ts).
 */
export const countingExtensions = [
  StarterKit.configure({
    heading: {
      levels: [1, 2, 3],
    },
    link: false,
  }),
  PolicyLink,
  ArticleImage,
  ...tableExtensions,
];

interface RichTextEditorProps {
  content?: string;
  onChange?: (json: string) => void;
  placeholder?: string;
  className?: string;
  editorClassName?: string;
  maxLength?: number;
}

interface RichTextViewerProps {
  content?: string | null;
  className?: string;
}

function parseContent(content: string): JSONContent | string {
  if (!content) return "";
  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === "object" && parsed.type === "doc") {
      return parsed as JSONContent;
    }
    return content;
  } catch {
    return content;
  }
}

export function richTextToPlainText(
  content: string | null | undefined,
): string {
  if (!content) return "";

  try {
    const parsed = JSON.parse(content);
    if (parsed && typeof parsed === "object" && parsed.type === "doc") {
      return generateText(parsed, countingExtensions);
    }
    return content;
  } catch {
    return content;
  }
}

/**
 * Normalizes a user-typed link target so it renders as intended. Absolute URLs,
 * protocol-relative URLs, `mailto:`/`tel:`/`sms:` schemes, root-relative paths
 * and fragment anchors pass through untouched; a bare host like
 * `example.com/path` gains an `https://` prefix so it resolves externally rather
 * than as a relative page link. Returns "" for empty input (caller unsets).
 */
export function normalizeLinkHref(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (/^(https?:)?\/\//iu.test(trimmed)) return trimmed;
  if (/^(mailto|tel|sms):/iu.test(trimmed)) return trimmed;
  if (/^[/?#]/u.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Toolbar control for inserting, editing and removing links. Clicking the button
 * opens a popover pre-filled with the current link's URL (when the cursor sits
 * on one) so authors can view and change the destination. Applying with an empty
 * URL removes the link; applying with no text selected inserts the URL as a new
 * linked text node.
 */
function LinkControl({ editor }: { editor: Editor | null }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");

  const isActive = editor?.isActive("link") ?? false;

  const handleOpenChange = (next: boolean) => {
    if (next) {
      const current = editor?.getAttributes("link").href;
      setUrl(typeof current === "string" ? current : "");
    }
    setOpen(next);
  };

  const applyLink = () => {
    if (!editor) return;
    const href = normalizeLinkHref(url);

    if (!href) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      setOpen(false);
      return;
    }

    const { from, to } = editor.state.selection;
    if (from === to && !editor.isActive("link")) {
      // Nothing selected and not on an existing link — insert the URL as its own
      // linked text node so the author ends up with a visible, clickable link.
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: href,
          marks: [{ type: "link", attrs: { href } }],
        })
        .run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    }
    setOpen(false);
  };

  const removeLink = () => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          title="Insert or edit link"
          className={`h-10 w-10 hover:bg-purple-50 ${
            isActive ? "bg-purple-100 text-primary" : "text-gray-400"
          }`}
        >
          <Link2 className="h-5 w-5" strokeWidth={isActive ? 3 : 2} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80"
        onCloseAutoFocus={(e) => {
          // Applying/removing moves focus into the editor; keep it there instead
          // of letting Radix yank it back to the trigger. On a keyboard cancel
          // (Escape) the editor isn't focused, so let Radix restore focus to the
          // trigger as expected for accessibility.
          if (editor?.isFocused) e.preventDefault();
        }}
      >
        <div className="flex flex-col gap-2">
          <label
            htmlFor="rich-text-link-url"
            className="text-sm font-medium text-gray-700"
          >
            Link URL
          </label>
          <Input
            id="rich-text-link-url"
            type="url"
            value={url}
            placeholder="https://example.com"
            onValueChange={setUrl}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyLink();
              }
            }}
          />
          <div className="flex items-center justify-between gap-2">
            {isActive ? (
              <Button
                type="button"
                variant="destructive-ghost"
                size="sm"
                onClick={removeLink}
              >
                <Unlink className="mr-1 h-4 w-4" />
                Remove
              </Button>
            ) : (
              <span />
            )}
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={applyLink}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Rich text editor with a character limit; onChange receives the document as a JSON string. */
export function RichTextEditor({
  content = "",
  onChange,
  placeholder = "Start writing...",
  className,
  editorClassName,
  maxLength,
}: RichTextEditorProps) {
  const initialContent = useMemo(() => parseContent(content), [content]);

  const [, forceUpdate] = useState(0);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        link: false,
      }),
      PolicyLink.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          class: "text-purple-600 underline",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      ArticleImage,
      ...tableExtensions,
      ...(maxLength ? [CharacterCount.configure({ limit: maxLength })] : []),
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      onChange?.(JSON.stringify(editor.getJSON()));
    },
    onSelectionUpdate: () => {
      forceUpdate((n) => n + 1);
    },
    onTransaction: () => {
      forceUpdate((n) => n + 1);
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none flex-1 p-4 focus:outline-none min-h-0 overflow-y-auto",
      },
    },
  });

  const characterCount = editor?.storage.characterCount?.characters() ?? 0;

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-xl border border-gray-200 transition focus-within:border-purple-500 focus-within:ring-2 focus-within:ring-ring",
        className,
      )}
    >
      <div className="flex items-center gap-4 border-b border-gray-100 bg-white px-4 py-2">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("heading", { level: 1 })
                ? "bg-purple-100 text-primary"
                : "text-gray-400"
            }`}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 1 }).run()
            }
          >
            <Heading1
              className="h-5 w-5"
              strokeWidth={editor?.isActive("heading", { level: 1 }) ? 3 : 2}
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("heading", { level: 2 })
                ? "bg-purple-100 text-primary"
                : "text-gray-400"
            }`}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <Heading2
              className="h-5 w-5"
              strokeWidth={editor?.isActive("heading", { level: 2 }) ? 3 : 2}
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("heading", { level: 3 })
                ? "bg-purple-100 text-primary"
                : "text-gray-400"
            }`}
            onClick={() =>
              editor?.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            <Heading3
              className="h-5 w-5"
              strokeWidth={editor?.isActive("heading", { level: 3 }) ? 3 : 2}
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("bold")
                ? "bg-purple-100 text-primary"
                : "text-gray-400"
            }`}
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Bold
              className="h-5 w-5"
              strokeWidth={editor?.isActive("bold") ? 3 : 2}
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("italic")
                ? "bg-purple-100 text-purple-900"
                : "text-gray-400"
            }`}
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Italic
              className="h-5 w-5"
              strokeWidth={editor?.isActive("italic") ? 3 : 2}
            />
          </Button>
          <LinkControl editor={editor} />
        </div>
        <div className="h-6 w-px bg-gray-200" />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("bulletList")
                ? "bg-purple-100 text-primary"
                : "text-gray-400"
            }`}
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <List
              className="h-5 w-5"
              strokeWidth={editor?.isActive("bulletList") ? 3 : 2}
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("orderedList")
                ? "bg-purple-100 text-primary"
                : "text-gray-400"
            }`}
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered
              className="h-5 w-5"
              strokeWidth={editor?.isActive("orderedList") ? 3 : 2}
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={`h-10 w-10 hover:bg-purple-50 ${
              editor?.isActive("table")
                ? "bg-purple-100 text-primary"
                : "text-gray-400"
            }`}
            onClick={() =>
              editor
                ?.chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
            title="Insert table"
          >
            <TableIcon
              className="h-5 w-5"
              strokeWidth={editor?.isActive("table") ? 3 : 2}
            />
          </Button>
        </div>
      </div>

      <EditorContent
        editor={editor}
        className={cn("flex-1 overflow-y-auto", editorClassName)}
      />

      {maxLength && (
        <div className="flex justify-end border-t border-gray-100 bg-white px-4 py-2">
          <span
            className={cn(
              "text-sm tabular-nums",
              characterCount > maxLength ? "text-red-500" : "text-gray-400",
            )}
          >
            {characterCount}/{maxLength}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Read-only display of stored rich text. Tiptap JSON renders through
 * `ArticleBody`, so the content is part of the server HTML that crawlers read.
 * A live editor only exists in the browser and would leave an empty block.
 */
export function RichTextViewer({ content, className }: RichTextViewerProps) {
  if (!content) return null;
  const doc = parseContent(content);

  return (
    <div className={cn("text-base text-gray-600", className)}>
      {typeof doc === "string" ? (
        <LegacyStringViewer content={doc} />
      ) : (
        <ArticleBody body={doc} className="prose prose-a:text-purple-600" />
      )}
    </div>
  );
}

/**
 * Content saved before the editor stored JSON is a plain string, which Tiptap
 * parses as HTML through the schema. That parse needs the browser's DOM, so
 * these strings still render client-side only.
 */
function LegacyStringViewer({ content }: { content: string }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        link: false,
      }),
      PolicyLink.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: "text-purple-600 underline",
        },
      }),
      ArticleImage,
      ...tableExtensions,
    ],
    content,
    editable: false,
    // Without this flag Tiptap throws during SSR in dev.
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "prose focus:outline-none",
      },
    },
  });

  return <EditorContent editor={editor} />;
}
